import { useMemo } from 'react';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useSWRConfig } from 'swr';

/**
 * 泛型定义：
 * @template ItemType 单条数据的类型 (例如 Product)
 * @template ResponseType 每一页接口返回的完整数据结构
 */
export interface UseSWRInfiniteScrollOptions<ItemType, ResponseType> {
  /**
   * 第一页的完整请求 Key。
   * 用于 SWR 在渲染前探测内存缓存，实现 0 延迟的后退位置恢复。
   * 必须是通过 `serializeKey` 序列化后的字符串。
   */
  firstPageKey: string | null;

  /**
   * SWRInfinite 的动态 Key 生成函数。
   * 注意：如果返回 null，SWR 将不会发起请求。
   */
  getKey: (pageIndex: number, previousPageData: ResponseType | null) => any[] | string | null;

  /**
   * 告诉 Hook 如何从一页的返回数据中提取出我们需要遍历的列表数组。
   * 如果该页没有数据，请返回空数组 []。
   */
  extractList: (pageData: ResponseType) => ItemType[];

  /**
   * 告诉 Hook 怎么判断已经没有更多数据了（是否触底）。
   * 例如：返回的列表长度小于 limit，或者返回的 totalCount 已经达到。
   */
  checkIsReachingEnd: (pageData: ResponseType) => boolean;

  /**
   * 触发请求的外部条件（通常传入 IntersectionObserver 的 inView 状态）。
   * 默认为 true。
   */
  inView?: boolean;

  /**
   * SWRInfinite 的原生配置透传
   */
  swrConfig?: SWRInfiniteConfiguration;
}

/**
 * 高性能的通用无限滚动 Hook (SWR 封装)
 *
 * 核心特性：
 * 1. 自动处理分页数据的打平 (Flatten)
 * 2. 内置 0 延迟缓存探针 (Cache Probing)，配合 persistSize 完美解决返回列表时的滚动条丢失问题。
 * 3. 自动计算是否触底 (isReachingEnd)
 */
export function useSWRInfiniteScroll<ItemType, ResponseType = any>({
  firstPageKey,
  getKey,
  extractList,
  checkIsReachingEnd,
  inView = true,
  swrConfig,
}: UseSWRInfiniteScrollOptions<ItemType, ResponseType>) {
  const { cache } = useSWRConfig();

  // 1. 核心：探测第一页是否在内存中
  const cachedState = firstPageKey ? cache.get(firstPageKey) : undefined;
  const hasCache = cachedState && cachedState.data !== undefined;

  // 2. 拦截 getKey：如果没有进入视口且没有缓存，强行返回 null 挂起请求
  const interceptedGetKey = (pageIndex: number, previousPageData: ResponseType | null) => {
    // 如果还没滚动到可视区域，且内存里也没有之前存下来的数据，就不发请求
    if (!inView && !hasCache) return null;
    return getKey(pageIndex, previousPageData);
  };

  // 3. 调用底层的 useSWRInfinite (不传 fetcher，利用全局 globalFetcher)
  const { data, error, isLoading, isValidating, size, setSize, mutate } =
    useSWRInfinite<ResponseType>(interceptedGetKey, {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateFirstPage: false,
      persistSize: true, // 核心：保持已加载的页数，配合 hasCache 实现后退 0 延迟渲染
      ...swrConfig,
    });

  // 4. 通用的数据打平逻辑
  const flattenedData = useMemo(() => {
    if (!data) return [];
    return data.reduce((acc: ItemType[], pageData) => {
      const list = extractList(pageData) || [];
      return [...acc, ...list];
    }, []);
  }, [data, extractList]);

  // 5. 通用的到底判断逻辑
  const isEmpty = data?.[0] ? extractList(data[0]).length === 0 : false;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1] ? checkIsReachingEnd(data[data.length - 1]) : false);

  return {
    data: flattenedData, // 提供给 UI 的一维数组
    originalData: data, // 原始的分页数据结构
    isLoading,
    isValidating,
    isReachingEnd,
    size,
    setSize,
    mutate,
    hasAttemptedFetch: data !== undefined || !!error,
    hasFetchError: !!error,
  };
}
